import { ReactElement, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbCircleDottedLetterN } from 'react-icons/tb';
import {
  HiOutlineTrash,
  HiOutlineDocumentText,
  HiOutlineDocumentMagnifyingGlass,
  HiOutlineLink,
  HiOutlineXCircle,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineClock,
} from 'react-icons/hi2';
import { dragFromAverages } from '../../../utils/drag';
import Button from '../../../components/Button';
import { createRun, getRuns, updateRun } from '../../../api/runs';
import { toast } from 'react-hot-toast';
import GridRow from 'components/GridRow';
import RunEditMenu from './RunEditMenu';
import { useFormContext } from 'react-hook-form';
import { EditOperationMode } from '../../SessionView/SessionDetails/SessionConfigs/editOperationUtils';

interface RunEntryProps {
  run: any;
  isNextRun: boolean;
  config: any;
  baselineDrag?: number;
  onDelete?: () => void;
  isEdit?: boolean;
  activeRunId?: string | null;
  setActiveRunId?: (id: string | null) => void;
  configs?: any[];
  invalidateRuns: any;
  sessions?: any[];
  hideColumns?: any;
  context?: 'runs' | 'sessions';
  alwaysLink?: boolean;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
  onReprocessRun?: () => void;
  rowBgClass?: string;
}

export default function RunEntry(props: RunEntryProps): ReactElement {
  const navigate = useNavigate();
  const {
    run,
    isNextRun,
    config,
    baselineDrag,
    onDelete,
    isEdit,
    activeRunId,
    setActiveRunId,
    configs,
    invalidateRuns,
    sessions,
    hideColumns,
    context,
    alwaysLink,
    configMovesToSave,
    editOpMode,
    setEditOpMode,
    onReprocessRun,
    rowBgClass,
  } = props;
  const [showRunsPopup, setShowRunsPopup] = useState(false);
  const [runsData, setRunsData] = useState<any[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [dateRange, setDateRange] = useState<'30_days' | '90_days' | 'all'>('30_days');
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [bucketName, setBucketName] = useState('');
  const [fileName, setFileName] = useState('');
  const [showNotesTooltip, setShowNotesTooltip] = useState(false);
  const [showMismatchTooltip, setShowMismatchTooltip] = useState(false);
  const drag = dragFromAverages(run);
  const delta = drag && baselineDrag && drag - baselineDrag;
  const isPlanned = !run.id;
  const buttonStyle =
    'cr-border-none cr-rounded-full cr-bg-transparent cr-text-cirrusBlue-100 hover:cr-bg-cirrusBlue-25 cr-shadow-none cr-transition-colors cr-duration-200 cr-cursor-pointer';
  const popupRef = useRef<HTMLDivElement>(null);

  const formMethods = useFormContext();

  // Check if config values match run values
  const checkValueMatches = () => {
    const mismatches: { field: string; configValue: any; runValue: any; severity: 'green' | 'yellow' | 'red' }[] = [];
    let worstSeverity: 'green' | 'yellow' | 'red' = 'green';

    // Check cp_corr
    // Red: > ±0.02, Yellow: ±0.005 to ±0.02, Green: < ±0.005
    const solveCpCorr = run?.solve?.cp_corr;
    if (config?.cp_corr !== undefined && solveCpCorr !== undefined && solveCpCorr !== null) {
      const diff = Math.abs(config.cp_corr - solveCpCorr);
      let severity: 'green' | 'yellow' | 'red' = 'green';

      if (diff >= 0.02) {
        severity = 'red';
      } else if (diff >= 0.005) {
        severity = 'yellow';
      }

      if (severity !== 'green') {
        mismatches.push({
          field: 'CP Correction',
          configValue: config.cp_corr,
          runValue: solveCpCorr,
          severity,
        });
        if (severity === 'red' || (worstSeverity === 'green' && severity === 'yellow')) {
          worstSeverity = severity;
        }
      }
    }

    // Check wheel_circumference
    // Red: > ±0.0015m, Yellow: ±0.0005m to ±0.0015m, Green: < ±0.0005m
    const solveWhlCrc = run?.solve?.wheel_circumference;
    if (config?.wheel_circumference !== undefined && solveWhlCrc !== undefined && solveWhlCrc !== null) {
      const diff = Math.abs(config.wheel_circumference - solveWhlCrc);
      let severity: 'green' | 'yellow' | 'red' = 'green';

      if (diff >= 0.0015) {
        severity = 'red';
      } else if (diff >= 0.0005) {
        severity = 'yellow';
      }

      if (severity !== 'green') {
        mismatches.push({
          field: 'Wheel Circumference',
          configValue: config.wheel_circumference,
          runValue: solveWhlCrc,
          severity,
        });
        if (severity === 'red' || (worstSeverity === 'green' && severity === 'yellow')) {
          worstSeverity = severity;
        }
      }
    }

    // Check yaw_offset
    if (config?.yaw_offset !== undefined && run?.yaw_offset !== undefined) {
      const diff = Math.abs(config.yaw_offset - run.yaw_offset);
      if (diff > 0.0001) {
        const severity: 'green' | 'yellow' | 'red' = 'red';
        mismatches.push({
          field: 'Yaw Offset',
          configValue: config.yaw_offset,
          runValue: run.yaw_offset,
          severity,
        });
        worstSeverity = 'red';
      }
    }

    return { worstSeverity, mismatches };
  };

  const valueMatchStatus = run?.id ? checkValueMatches() : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setActiveRunId?.(null);
      }
    };

    if (activeRunId === run.id) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeRunId, run.id, setActiveRunId]);

  const fetchRunsData = async (dateRangeParam?: '30_days' | '90_days' | 'all') => {
    setIsLoadingRuns(true);
    try {
      const dates = dateRangeParam || dateRange;
      const response = await getRuns(`scope=need_processing&dates=${dates}`);

      // getData returns the array directly
      const runs = Array.isArray(response) ? response : [];

      setRunsData(runs);
    } catch (error) {
      console.error('Error fetching runs data:', error);
      toast.error('Error fetching runs data');
    } finally {
      setIsLoadingRuns(false);
    }
  };

  const handleDateRangeChange = (newRange: '30_days' | '90_days' | 'all') => {
    setDateRange(newRange);
    fetchRunsData(newRange);
  };

  const handleLinkButtonClick = async () => {
    setShowRunsPopup(true);
    setIsManualEntry(false);
    setBucketName('');
    setFileName('');
    await fetchRunsData(dateRange);
  };

  const handleManualEntrySubmit = async () => {
    if (!bucketName || !fileName) {
      toast.error('Please enter both bucket name and file name');
      return;
    }

    try {
      // Use current timestamp for run_ts
      const now = new Date();
      const runTimestamp = now.toISOString();

      const payload = {
        config_id: config.id,
        run_ts: runTimestamp,
        file_name: fileName,
        bucket_name: bucketName,
        rider_id: formMethods.getValues('rider_id'),
        description: config.description,
      };

      // Make POST request to create run
      await createRun(payload);

      // Close popup and refresh session data
      setShowRunsPopup(false);
      setBucketName('');
      setFileName('');
      invalidateRuns();

      toast.success('Run created successfully');
    } catch (error) {
      console.error('Error creating manual run:', error);
      toast.error('Error creating manual run');
    }
  };

  const createNewRunWithExistingData = async (runData: any) => {
    try {
      // Combine date and time to create ISO datetime string
      const runTimestamp = `${runData.date}T${runData.time}`;

      const payload = {
        run_ts: runTimestamp,
        rider_id: formMethods.getValues('rider_id'),
        description: config.description,
        calibration: config.calibration_id,
        run_number: run?.run_number,
        config_id: config.id,
        config: run?.config,
        session: formMethods.getValues('session'),
      };

      // Make POST request to create run
      const runResponse = await createRun(payload);

      const runPayload = {
        id: runResponse.id,
        bucket_name: runData.bucket_name,
        file_name: runData.file_name,
      };

      updateRun(runResponse.id, runPayload);

      // Close popup and refresh session data
      setShowRunsPopup(false);
      invalidateRuns();
    } catch (error) {
      console.error('Error creating run or group association:', error);
      toast.error('Error creating run or group association');
    }
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or menus
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    // Don't navigate if clicking on popup menus
    // Note: editMenuRef is now handled in RunEditMenu component
    if (popupRef.current && popupRef.current.contains(e.target as Node)) {
      return;
    }

    // Only navigate for non-planned runs and when not in edit mode (unless alwaysLink is true)
    if (run.id && !isPlanned && (!isEdit || alwaysLink)) {
      // Use session-aware route if we're in a session context
      if (context === 'sessions' && config?.testsession_id) {
        navigate(`/sessions/${config?.testsession_id}/runs/${run.id}`);
      } else {
        navigate(`/runs/${run.id}`);
      }
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .cr-animate-fade-in {
            animation: fadeIn 0.3s ease-out;
          }
        `}
      </style>
      <GridRow
        className={`${isPlanned && 'cr-italic cr-text-gray-500'} cr-relative ${rowBgClass || ''} ${
          !isPlanned && run.id && (!isEdit || alwaysLink)
            ? 'cr-cursor-pointer cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50'
            : ''
        }`}
        onClick={!isPlanned && run.id && (!isEdit || alwaysLink) ? handleRowClick : undefined}
      >
        {!hideColumns?.spacer ? (
          <div className="cr-flex cr-items-center cr-justify-center cr-gap-1 !cr-border-b-0">
            {run.run_status && (
              <>
                {run.run_status === 4000 && <HiOutlineCheckCircle className="cr-size-5 cr-text-green-500" />}
                {run.run_status > 3000 && run.run_status < 4000 && (
                  <HiOutlineExclamationTriangle className="cr-size-5 cr-text-red-500" />
                )}
                {run.run_status < 3000 && <HiOutlineClock className="cr-size-5 cr-text-orange-500" />}
              </>
            )}
            {/* Show asterisk for value match/mismatch status */}
            {valueMatchStatus && (
              <div className="cr-relative">
                <span
                  className={`cr-cursor-help cr-text-lg cr-font-bold ${
                    valueMatchStatus.worstSeverity === 'green'
                      ? 'cr-text-green-500'
                      : valueMatchStatus.worstSeverity === 'yellow'
                        ? 'cr-text-yellow-500'
                        : 'cr-text-red-500'
                  }`}
                  onMouseEnter={() => setShowMismatchTooltip(true)}
                  onMouseLeave={() => setShowMismatchTooltip(false)}
                >
                  *
                </span>
                {showMismatchTooltip && valueMatchStatus.mismatches.length > 0 && (
                  <div className="cr-absolute cr-bottom-full cr-left-1/2 cr-z-50 cr-mb-2 cr-w-72 cr--translate-x-1/2 cr-transform cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white cr-p-3 cr-shadow-lg">
                    <div className="cr-mb-2 cr-text-sm cr-font-semibold cr-text-gray-700">Value Mismatches:</div>
                    <div className="cr-space-y-1">
                      {valueMatchStatus.mismatches.map((mismatch, index) => (
                        <div key={index} className="cr-text-sm cr-text-gray-700">
                          <span className="cr-font-medium cr-flex cr-items-center cr-gap-1">
                            {mismatch.field}:
                            <span
                              className={`cr-inline-block cr-h-2 cr-w-2 cr-rounded-full ${
                                mismatch.severity === 'red'
                                  ? 'cr-bg-red-500'
                                  : mismatch.severity === 'yellow'
                                    ? 'cr-bg-yellow-500'
                                    : 'cr-bg-green-500'
                              }`}
                            ></span>
                          </span>
                          <div className="cr-ml-2 cr-text-xs">
                            <div>
                              Calibration Value:{' '}
                              {typeof mismatch.configValue === 'number'
                                ? mismatch.configValue.toFixed(4)
                                : mismatch.configValue}
                            </div>
                            <div>
                              Run Solve Value:{' '}
                              {typeof mismatch.runValue === 'number' ? mismatch.runValue.toFixed(4) : mismatch.runValue}
                            </div>
                            <div>Reprocess Run to Fix</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {showMismatchTooltip && valueMatchStatus.worstSeverity === 'green' && valueMatchStatus.mismatches.length === 0 && (
                  <div className="cr-absolute cr-bottom-full cr-left-1/2 cr-z-50 cr-mb-2 cr-w-48 cr--translate-x-1/2 cr-transform cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white cr-p-3 cr-shadow-lg">
                    <div className="cr-text-sm cr-text-gray-700">Calibration & Solve Values Match</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div></div>
        )}
        <div className="cr-flex cr-items-center cr-justify-center cr-pl-0 cr-text-center">
          {!hideColumns?.run_number && <div>{run.run_number}</div>}
        </div>
        <div className="cr-flex cr-items-center cr-justify-center cr-pl-0 cr-text-center">
          <div className="cr-text-center">
            {run.id
              ? ((): string => {
                  // Use run_ts_tz if available, fallback to run_ts
                  const timestamp = run.run_ts_tz || run.run_ts;
                  if (!timestamp) return 'NA';

                  const runDate = new Date(timestamp);
                  // Check if date is valid
                  if (isNaN(runDate.getTime())) return 'Invalid Date';

                  const sessionDate = new Date(formMethods.getValues('date'));

                  // Format time as HH:MM
                  const timeString = runDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  });

                  // Format date
                  const dateString = runDate.toLocaleDateString('en-UK', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  });

                  // If viewed from runs page, always show full date and time
                  if (context === 'runs') {
                    return `${dateString} ${timeString}`;
                  }

                  // For sessions view, check if run date is different from session date
                  const runDateString = runDate.toDateString();
                  const sessionDateString = sessionDate.toDateString();

                  if (runDateString !== sessionDateString) {
                    // Show date and time if different
                    return `${dateString} ${timeString}`;
                  } else {
                    // Show only time if same date
                    return timeString;
                  }
                })()
              : 'NA'}
          </div>
        </div>
        <div className="cr-flex cr-items-center cr-justify-center cr-break-all">
          {!hideColumns?.drag && (
            <div className="cr-flex cr-items-center cr-gap-1">
              <span>{drag && drag.toFixed(3)}</span>
              {run.notes && run.notes.trim() !== '' && (
                <div className="cr-relative">
                  <HiOutlineDocumentText
                    className="cr-text-cirrusBlue-600 cr-size-4 cr-cursor-help"
                    onMouseEnter={() => setShowNotesTooltip(true)}
                    onMouseLeave={() => setShowNotesTooltip(false)}
                  />
                  {showNotesTooltip && (
                    <div className="cr-absolute cr-bottom-full cr-left-1/2 cr-z-50 cr-mb-2 cr-max-h-48 cr-w-64 cr--translate-x-1/2 cr-transform cr-overflow-y-auto cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white cr-p-3 cr-shadow-lg">
                      <div className="cr-mb-2 cr-text-sm cr-font-semibold cr-text-gray-700">Run Notes:</div>
                      <div className="cr-whitespace-pre-wrap cr-break-words cr-text-left cr-text-sm cr-text-gray-700">
                        {run.notes}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="cr-flex cr-items-center cr-justify-center cr-break-all">
          {!hideColumns?.delta && delta && (
            <span className={delta < 0 ? 'cr-text-blue-500' : 'cr-text-red'}>
              {delta > 0 ? '+' : ''}
              {delta.toFixed(3)}
            </span>
          )}
        </div>
        <div className="cr-flex cr-justify-end cr-break-all">
          {!hideColumns?.nextRun && isNextRun && <TbCircleDottedLetterN size={35} />}
        </div>
        <div className="cr-relative cr-break-all">
          {isPlanned && onDelete && isEdit && (
            <div className="cr-flex cr-gap-1">
              <Button
                className={buttonStyle}
                onClick={handleLinkButtonClick}
                disabled={isLoadingRuns}
                icon={<HiOutlineLink className="cr-size-5" />}
              />
              <Button className={buttonStyle} onClick={onDelete} icon={<HiOutlineTrash className="cr-size-5" />} />
            </div>
          )}
          {/* Run Edit Menu */}
          {!isPlanned && isEdit && (
            <RunEditMenu
              run={run}
              config={config}
              configs={configs}
              sessions={sessions}
              invalidateRuns={invalidateRuns}
              configMovesToSave={configMovesToSave}
              buttonStyle={buttonStyle}
              editOpMode={editOpMode}
              setEditOpMode={setEditOpMode}
              onReprocessRun={onReprocessRun}
            />
          )}
          {/* Run Details Button */}
          {!isEdit && !isPlanned && (
            <Button
              className={buttonStyle}
              onClick={() => {
                if (context === 'sessions' && config?.testsession_id) {
                  navigate(
                    `/sessions/${config.testsession_id}/runs/${run.id}/overview`
                  );
                } else {
                  navigate(`/runs/${run.id}/overview`);
                }
              }}
              icon={<HiOutlineDocumentMagnifyingGlass className="cr-size-5" />}
            />
          )}
        </div>
      </GridRow>
      {/* Run Link Popup */}
      {showRunsPopup && (
        <div className="cr-fixed cr-inset-0 cr-z-50 cr-flex cr-items-center cr-justify-center cr-bg-black cr-bg-opacity-50">
          <div className="cr-max-h-[80vh] cr-w-[50vw] cr-max-w-lg cr-overflow-hidden cr-rounded-lg cr-bg-white cr-shadow-xl">
            <div className="cr-flex cr-items-center cr-justify-between cr-border-b cr-p-4">
              <h2 className="cr-text-lg cr-font-semibold">
                {isManualEntry ? 'Manual Entry' : 'Runs Needing Processing'}
              </h2>
              <Button
                className="cr-text-gray-500 hover:cr-text-gray-700"
                onClick={() => setShowRunsPopup(false)}
                icon={<HiOutlineXCircle className="cr-size-6" />}
              />
            </div>
            {!isManualEntry && (
              <div className="cr-border-b cr-p-4">
                <div className="cr-flex cr-items-center cr-gap-4">
                  <span className="cr-text-sm cr-font-medium cr-text-gray-700">Date Range:</span>
                  <label className="cr-flex cr-items-center cr-gap-2">
                    <input
                      type="radio"
                      name="dateRange"
                      value="30_days"
                      checked={dateRange === '30_days'}
                      onChange={() => handleDateRangeChange('30_days')}
                      className="cr-text-cirrusBlue-500 cr-focus:cr-ring-cirrusBlue-500"
                    />
                    <span className="cr-text-sm cr-text-gray-700">30 Days</span>
                  </label>
                  <label className="cr-flex cr-items-center cr-gap-2">
                    <input
                      type="radio"
                      name="dateRange"
                      value="90_days"
                      checked={dateRange === '90_days'}
                      onChange={() => handleDateRangeChange('90_days')}
                      className="cr-text-cirrusBlue-500 cr-focus:cr-ring-cirrusBlue-500"
                    />
                    <span className="cr-text-sm cr-text-gray-700">90 Days</span>
                  </label>
                  <label className="cr-flex cr-items-center cr-gap-2">
                    <input
                      type="radio"
                      name="dateRange"
                      value="all"
                      checked={dateRange === 'all'}
                      onChange={() => handleDateRangeChange('all')}
                      className="cr-text-cirrusBlue-500 cr-focus:cr-ring-cirrusBlue-500"
                    />
                    <span className="cr-text-sm cr-text-gray-700">All</span>
                  </label>
                </div>
              </div>
            )}
            <div className="cr-max-h-[60vh] cr-overflow-auto cr-p-4">
              {isManualEntry ? (
                <div className="cr-space-y-4">
                  <div>
                    <label className="cr-mb-2 cr-block cr-text-sm cr-font-medium cr-text-gray-700">Bucket Name</label>
                    <input
                      type="text"
                      value={bucketName}
                      onChange={(e) => setBucketName(e.target.value)}
                      className="cr-focus:cr-border-cirrusBlue-500 cr-focus:cr-outline-none cr-focus:cr-ring-1 cr-focus:cr-ring-cirrusBlue-500 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm"
                      placeholder="Enter bucket name"
                    />
                  </div>
                  <div>
                    <label className="cr-mb-2 cr-block cr-text-sm cr-font-medium cr-text-gray-700">File Name</label>
                    <input
                      type="text"
                      value={fileName}
                      onChange={(e) => setFileName(e.target.value)}
                      className="cr-focus:cr-border-cirrusBlue-500 cr-focus:cr-outline-none cr-focus:cr-ring-1 cr-focus:cr-ring-cirrusBlue-500 cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm"
                      placeholder="Enter file name (e.g., 2024_0115_143052)"
                    />
                  </div>
                  <div className="cr-mt-6">
                    <Button
                      className="cr-bg-cirrusBlue-500 hover:cr-bg-cirrusBlue-600 cr-w-full cr-rounded cr-px-4 cr-py-2 cr-text-white"
                      onClick={handleManualEntrySubmit}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              ) : isLoadingRuns ? (
                <div className="cr-flex cr-items-center cr-justify-center cr-py-8">
                  <div className="cr-text-gray-500">Loading runs...</div>
                </div>
              ) : Array.isArray(runsData) && runsData.length > 0 ? (
                <table className="cr-w-full cr-border-collapse cr-border cr-border-gray-300">
                  <thead>
                    <tr className="cr-bg-gray-50">
                      <th className="cr-border cr-border-gray-300 cr-px-4 cr-py-2 cr-text-left">Date</th>
                      <th className="cr-border cr-border-gray-300 cr-px-4 cr-py-2 cr-text-left">Time</th>
                      <th className="cr-border cr-border-gray-300 cr-px-4 cr-py-2 cr-text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runsData.map((runData: any, index: number) => (
                      <tr key={runData.file_name || index} className="hover:cr-bg-gray-50">
                        <td className="cr-border cr-border-gray-300 cr-px-4 cr-py-2 cr-text-left">
                          {runData.date || 'N/A'}
                        </td>
                        <td className="cr-border cr-border-gray-300 cr-px-4 cr-py-2 cr-text-left">
                          {runData.time || 'N/A'}
                        </td>
                        <td className="cr-border cr-border-gray-300 cr-px-4 cr-py-2 cr-text-left">
                          <Button
                            className="hover:cr-bg-cirrusBlue-200 cr-text-cirrusBlue-700 cr-rounded cr-bg-cirrusBlue-100 cr-px-3 cr-py-1 cr-text-sm"
                            onClick={() => createNewRunWithExistingData(runData)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="cr-flex cr-items-center cr-justify-center cr-py-8">
                  <div className="cr-text-gray-500">No runs found</div>
                </div>
              )}
            </div>
            {!isManualEntry && (
              <div className="cr-border-t cr-p-4">
                <Button
                  className="cr-w-full cr-rounded cr-bg-gray-100 cr-px-4 cr-py-2 cr-text-gray-700 hover:cr-bg-gray-200"
                  onClick={() => setIsManualEntry(true)}
                >
                  Add Manually
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
